<template>
  <div class="forecast-container" :class="{ locked: templateLocked }">
    <div class="page-title">隧道预报填报进度</div>

    <!-- 模板格式错误：锁定界面 -->
    <div v-if="templateLocked" class="template-error">
      <p class="template-error-title">模板格式错误</p>
      <p class="template-error-msg">{{ integrityMessage }}</p>
      <p v-if="missingLabels.length > 0" class="template-error-sub">缺失控件：</p>
      <ul v-if="missingLabels.length > 0">
        <li v-for="(label, index) in missingLabels" :key="index">{{ label }}</li>
      </ul>
    </div>

    <template v-else>
      <!-- 进度条 -->
      <div class="progress-box">
        <div class="progress-text">
          当前填报完成度：{{ progress }}%（必填项：{{ requiredProgress }}%）
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progress + '%' }"></div>
        </div>
      </div>
      <!-- 必填项明细 -->
      <div class="require-tip">
        <p>必填填报项：</p>
        <ul>
          <li v-for="(item, index) in requireFields" :key="index">
            {{ item.name }}：<span :class="{ finish: item.finish, unfinish: !item.finish }">{{
              item.finish ? '已填写' : '未填写'
            }}</span>
          </li>
        </ul>
      </div>
      <!-- 验证错误提示 -->
      <div v-if="submitErrorList.length > 0" class="error-tip">
        <p>提交验证问题：</p>
        <ul>
          <li v-for="(err, index) in submitErrorList" :key="index">{{ err }}</li>
        </ul>
      </div>
      <!-- 上报按钮 -->
      <button class="report-btn" @click="submitReport" :disabled="requiredProgress < 100">
        上报预报数据
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import request from '@/utils/request'
import { notify } from '@/utils/notify'
import { getDocProperties, getDocProperty } from '@/utils/docProperties'
import {
  readFaceTemplateFromDoc,
  getImageBase64FromDoc,
  subscribeFaceTemplateControlChanges,
  type FaceTemplateControlSubscription,
} from '@/utils/FaceTemplateUtils'
import type { FaceTemplate, FieldCheckResult } from '@/model/FaceTemplate'

const REFRESH_DEBOUNCE_MS = 300

const progress = ref(0)
const requiredProgress = ref(0)
const faceTemplate = ref<FaceTemplate | null>(null)
const fieldResults = ref<FieldCheckResult[]>([])
const requireFields = ref<{ name: string; finish: boolean }[]>([])
const submitErrorList = ref<string[]>([])
const templateLocked = ref(false)
const integrityMessage = ref('')
const missingLabels = ref<string[]>([])

let subscription: FaceTemplateControlSubscription | null = null
let debounceTimer: number | null = null
let refreshing = false
let pendingRefresh = false
let disposed = false

async function disposeSubscription() {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  if (!subscription) return
  const current = subscription
  subscription = null
  await current.dispose()
}

function applyIntegrityLock(message: string, labels: string[]) {
  templateLocked.value = true
  integrityMessage.value = message
  missingLabels.value = labels
  progress.value = 0
  requiredProgress.value = 0
  requireFields.value = []
  submitErrorList.value = []
  void disposeSubscription()
}

async function readDocBaseProperties() {
  const props = await getDocProperties()
  console.log(props.title, props.keywords)
  await getDocProperty('keywords')
}

async function refreshFromDoc(options?: { notifyOnLock?: boolean }) {
  if (disposed) return
  if (refreshing) {
    pendingRefresh = true
    return
  }
  refreshing = true
  try {
    const result = await readFaceTemplateFromDoc()
    if (disposed) return

    if (!result.integrity.valid) {
      applyIntegrityLock(result.integrity.message, result.integrity.missingLabels)
      if (options?.notifyOnLock !== false) {
        notify.error('Word 模板格式错误，界面已锁定')
      }
      return
    }

    templateLocked.value = false
    faceTemplate.value = result.template
    progress.value = result.completeness
    requiredProgress.value = result.requiredCompleteness
    fieldResults.value = result.fields
    requireFields.value = result.fields
      .filter((f) => f.required)
      .map((f) => ({ name: f.label, finish: f.filled }))
    submitErrorList.value = result.submitErrors.map((e) => e.message)
  } catch (err) {
    console.error('读取填报进度失败', err)
  } finally {
    refreshing = false
    if (pendingRefresh && !disposed && !templateLocked.value) {
      pendingRefresh = false
      void refreshFromDoc({ notifyOnLock: false })
    } else {
      pendingRefresh = false
    }
  }
}

function scheduleRefreshFromDoc() {
  if (disposed || templateLocked.value) return
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = window.setTimeout(() => {
    debounceTimer = null
    void refreshFromDoc({ notifyOnLock: false })
  }, REFRESH_DEBOUNCE_MS)
}

async function startControlWatching() {
  if (disposed || templateLocked.value || subscription) return
  try {
    subscription = await subscribeFaceTemplateControlChanges({
      onChange: () => scheduleRefreshFromDoc(),
      // 删除必要控件：立即重读完整性并锁定（不走 debounce）
      onDeleted: () => {
        void refreshFromDoc({ notifyOnLock: true })
      },
    })
  } catch (err) {
    console.error('注册内容控件监听失败，侧边栏将仅在进入/上报时刷新', err)
  }
}

const submitReport = async () => {
  if (templateLocked.value) {
    notify.error('模板格式错误，无法上报')
    return
  }

  const result = await readFaceTemplateFromDoc()
  if (!result.integrity.valid) {
    applyIntegrityLock(result.integrity.message, result.integrity.missingLabels)
    notify.error('Word 模板格式错误，界面已锁定')
    return
  }

  faceTemplate.value = result.template
  progress.value = result.completeness
  requiredProgress.value = result.requiredCompleteness
  fieldResults.value = result.fields
  requireFields.value = result.fields
    .filter((f) => f.required)
    .map((f) => ({ name: f.label, finish: f.filled }))

  if (result.submitErrors.length > 0) {
    submitErrorList.value = result.submitErrors.map((e) => e.message)
    notify.warning(`存在 ${result.submitErrors.length} 项验证问题，请修正后再提交`)
    console.log('提交验证失败：', result.submitErrors)
    return
  }

  console.log('FaceTemplate 用户填写值：', JSON.stringify(result.template, null, 2))

  try {
    const sumiaoBase64 = await getImageBase64FromDoc('sumiao')
    const facePictureBase64 = await getImageBase64FromDoc('facePicture')
    console.log('素描图 base64长度：', sumiaoBase64.length)
    console.log('掌子面图 base64长度：', facePictureBase64.length)
    const res = await request.post('/face/template/cg', result.template)
    notify.success('预报数据上报成功！')
    console.log('上报结果', res.data)
  } catch (err) {
    notify.error('数据上报失败，请重试')
    console.error(err)
  }
}

onMounted(async () => {
  disposed = false
  void readDocBaseProperties()
  await refreshFromDoc({ notifyOnLock: true })
  if (!templateLocked.value) {
    await startControlWatching()
  }
})

onUnmounted(() => {
  disposed = true
  void disposeSubscription()
})
</script>

<style scoped>
.forecast-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.forecast-container.locked {
  pointer-events: none;
  user-select: none;
}

.page-title {
  font-size: 22px;
  color: #333;
  font-weight: 500;
}

.template-error {
  padding: 16px;
  background-color: #fff7f7;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  pointer-events: auto;
}

.template-error-title {
  margin-bottom: 8px;
  color: #f53f3f;
  font-size: 16px;
  font-weight: 600;
}

.template-error-msg {
  margin-bottom: 12px;
  color: #333;
  font-size: 14px;
  line-height: 1.5;
}

.template-error-sub {
  margin-bottom: 6px;
  color: #666;
  font-size: 13px;
}

.template-error ul {
  max-height: 240px;
  overflow-y: auto;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.template-error li {
  color: #f53f3f;
  font-size: 13px;
}

.progress-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.progress-text {
  font-size: 15px;
  color: #333;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #1677ff;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.require-tip {
  padding: 16px;
  background-color: #f7faff;
  border-radius: 8px;
}

.require-tip p {
  margin-bottom: 8px;
  color: #333;
}

.require-tip ul {
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.require-tip li span.finish {
  color: #00b42a;
  font-weight: 500;
}

.require-tip li span.unfinish {
  color: #f53f3f;
}

.error-tip {
  padding: 16px;
  background-color: #fff7f7;
  border: 1px solid #ffccc7;
  border-radius: 8px;
}

.error-tip p {
  margin-bottom: 8px;
  color: #f53f3f;
  font-weight: 500;
}

.error-tip ul {
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.error-tip li {
  color: #f53f3f;
  font-size: 13px;
}

.report-btn {
  height: 48px;
  background-color: #1677ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  margin-top: 10px;
  cursor: pointer;
  margin-bottom: 25px;
  transition:
    opacity 0.2s,
    background-color 0.2s;
}

.report-btn:not(:disabled):hover {
  background-color: #0e5fd8;
}

.report-btn:disabled {
  background-color: #c9c9c9;
  color: #fff;
  cursor: not-allowed;
  opacity: 0.65;
}
</style>
